import React, { useState } from "react"
import { graphql } from "gatsby"
import { StposYaml, ModulesYaml } from "../../types/graphql-types"
import Container from "./container"
import Foldable from "./foldable"
import { Creditpoints } from "./creditpoints"
import { PartsList } from "./parts"
import { KindList } from "./kinds"
import StpoSelector from "./stpos"
import { CategoryHeading, categories } from "./categories"

export const modulesScheduleQuery = graphql`
  fragment ModulesSchedule on ModulesYamlSchedule {
    semester
    start
    stpo {
      ...StPOs
    }
  }
`

export const modulesQuery = graphql`
  fragment Modules on ModulesYaml {
    category
    code
    id
    creditpoints
    regularity {
      kind
      note
    }
    schedule {
      ...ModulesSchedule
    }
    title {
      english
      german
    }
    kinds {
      ...ModulesKinds
    }
    parts {
      ...ModulesParts
    }
  }
`

interface Module {
  module: ModulesYaml
}

export const ModuleDetails: React.FC<Module> = ({ module }) => {
  return (
    <div class="text-umr-grey-600 mt-4">
      {module.kinds ? (
        <KindList kinds={module.kinds} />
      ) : (
        <>
          <PartsList parts={module.parts} />
        </>
      )}
      <Creditpoints creditpoints={module.creditpoints} />
      <p class="my-2">
        {module.regularity.kind ? (
          <>Diese Veranstaltung findet {module.regularity.kind} statt.</>
        ) : "" }
      </p>
      {module.regularity.note ? (
        <p>{ module.regularity.note }</p>
      ) : ""}
    </div>
  )
}

export const ModuleHeading: React.FC<Module> = ({ module }) => (
  <>
    <h2 class="inline-block text-xl">
      {module.title.german}{" "}
      <div class="inline-block text-umr-grey-400">({module.code})</div>
    </h2>
    <div class="text-lg text-umr-grey-400">{module.title.english}</div>
  </>
)

export const Module: React.FC<Module> = ({ module }) => (
  <Foldable
    heading={<ModuleHeading module={module} />}
    details={<ModuleDetails module={module} />}
  />
)

interface ModulesContainer {
  modules: ModulesYaml[]
  activeStpo?: StposYaml | null
  category: string
  className: string
}

export const ModulesContainer: React.FC<ModulesContainer> = ({
  modules,
  activeStpo,
  category,
  className,
}) => (
  <Container
    heading={<CategoryHeading activeStpo={activeStpo} category={category} />}
    className={className}
    collection={modules}
    filter={(module: ModulesYaml) => module.category === category}
    order={(a, b) => a.schedule[0].semester - b.schedule[0].semester}
    item={(module: ModulesYaml) => (
      <li
        key={module.id}
        class={`${
          module?.schedule.some(s => s.stpo.id === activeStpo?.id)
            ? ""
            : "hidden"
        } px-4 py-4 mb-4 last-child-mb-0 hover:bg-gray-100 bg-white shadow-lg transition-colors duration-500`}
      >
        <Module module={module} />
      </li>
    )}
  />
)

interface Modules {
  modules: ModulesYaml[]
  stpos: StposYaml[]
  className?: string
}

export const Modules: React.FC<Modules> = ({
  modules,
  stpos,
  className = "",
}) => {
  const [activeStpo, setActiveStpo] = useState(stpos[stpos.length - 1])

  return (
    <div class={className}>
      <StpoSelector
        stpos={stpos}
        activeStpo={activeStpo}
        setActiveStpo={setActiveStpo}
      />
      <div class="pl-4 bg-umr-grey-100 flex flex-wrap">
        {categories.map(category => (
          <ModulesContainer
            key={category}
            modules={modules}
            className="w-full md:w-1/2 mb-4 p-4 pl-0 last-child-mb-0 md:second-last-child-mb-0"
            activeStpo={activeStpo}
            category={category}
          />
        ))}
      </div>
    </div>
  )
}
